import { useEffect, useState } from "react";
import "../styles/PromotionsPage.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../redux/promotionsSlice";
import { Expand, Heart } from "lucide-react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
    addToWishlist,
    removeFromWishlist,
} from "../../../../components/Wishlist/WishlistSlice";
import { Slide, ToastContainer, toast } from "react-toastify";
import { Player } from "@lottiefiles/react-lottie-player";
import heartAnimation from "../../../../assets/icons/Heart.json";
import LoginPromptModal from "../../../../components/LoginModal/LoginPromptModal";
import Footer from "../../../../components/Footer/Footer";
import Pagination from "../../../../components/Pagination/Pagination";
import emptyproduct from "../../../../assets/images/empty-product.png";
import { useCartWishlist } from "../../../../app/CartWishlistContext";
import TopPickSection from "../../topPicks/pages/topPickPage";
import CustomerFavourite from "../components/CustomerFavourite";
import { getPaginationMeta } from "../../../../utils/pagination.utils";
import EmptyProducts from "../components/EmptyProducts";
import SkeletonProduct from "../components/skeletons/SkeletonProduct";
import { capitalizeWords, limitChars, capitalizeWordsDash, orderByString } from "../../../../utils/text.utils";
import { formatDiscount, formatPrice } from "../../../../utils/price.utils";
import { getProductPdpUrl } from "../../../../utils/url.utils";
import SkeletonFilter from "../components/skeletons/SkeletonFilter";
import PromotionBreadcrumbs from "../../../../components/Breadcrumbs/PromotionBreadcrumbs";
import MobileFilter from "../../promotion/components/MobileFilters";
import useBestsellerBanner from "../hooks/useBestsellerBanner";
import { Fragment } from "react";
import 'react-lazy-load-image-component/src/effects/blur.css';
import ProductQuickViewModal from "../../../quickView/pages/ProductsQuickViewModal";

const PromotionsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { getCartWishlistCount } = useCartWishlist();
    const { promotionType = "search" } = useParams();
    const [products, setProducts] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [quickViewProduct, setQuickViewProduct] = useState(null);
    const [animatedWish, setAnimatedWish] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempMobileFilters, setTempMobileFilters] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});
    const [selected, setSelected] = useState("");
    const [showShort, setShowShort] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const queryText = searchParams.get('q', null);
    const currentPage = Number(searchParams.get("page")) || 1;
    /*  SHOW BANNER ONLY ON BESTSELLERS */
    const isBestsellerPage = promotionType?.toLowerCase() === "bestsellers";
    const { banner, status: bannerStatus } = useBestsellerBanner();

    const { data, filters, sorting, pagination, loading } = useSelector((state) => state.products);
    const [dataReady, setDataReady] = useState(true);
    const {
        total,
        totalPages,
        rangeStart,
        rangeEnd,
    } = getPaginationMeta(pagination, currentPage);
    const filterTitle = capitalizeWordsDash(promotionType);
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
    useEffect(() => {
        if (!hasFetchedOnce) return; //  BLOCK first render
        if (loading) {
            setDataReady(true);
            return;
        }

        if (Array.isArray(data)) {
            setProducts(data);
            setDataReady(false);
        }
    }, [data, loading, hasFetchedOnce]);


    useEffect(() => {
    }, [location.pathname]); // runs on route change

    useEffect(() => {
        document.title = `Offers | obsessions.co.in`;
        const token = localStorage.getItem("token");
        setIsLoggedIn(Boolean(token));

        const urlFilters = getFiltersFromURL(location.search);
        setSelectedFilters(urlFilters);
    }, [location.search]);


    const getFiltersFromURL = (search) => {
        const params = new URLSearchParams(search);
        const filters = {};

        for (const [key, value] of params.entries()) {
            if (key === "page" || key === "sort") continue;
            const decoded = decodeURIComponent(value.replace(/\+/g, " "));
            filters[key] = decoded.includes(",") ? decoded.split(",").map(v => v.trim()) : [decoded.trim()];
        }
        return filters;
    };
    useEffect(() => {
        const urlFilters = getFiltersFromURL(location.search);

        setSelectedFilters((prev) => {
            const prevString = JSON.stringify(prev);
            const nextString = JSON.stringify(urlFilters);

            if (prevString === nextString) {
                return prev; // prevent re-render + API call
            }

            return urlFilters;
        });
    }, [location.search]);

useEffect(() => {
    if (!promotionType) return;

    setDataReady(true);

    dispatch(
        fetchProducts({
            promotionType,
            page: currentPage,
            limit: 40,
            filters: selectedFilters || {},  
            q: queryText || "" 
        })
    )
        .unwrap()
        .finally(() => {
            setHasFetchedOnce(true);
        });

}, [promotionType, selectedFilters, currentPage, queryText, dispatch]);

useEffect(() => {
    if (!queryText) return;

    setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", 1);   //  reset page
        return params;
    });

}, [queryText]);

useEffect(() => {
    if (!queryText) return;

    setSelectedFilters({});
    setTempMobileFilters({});

}, [queryText]);


    const handlePageChange = (page) => {
        const nextPage = Math.min(
            Math.max(page, 1),
            totalPages
        );

        const params = Object.fromEntries(searchParams);
        params.page = nextPage;
        setSearchParams(params);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const handleFilterChange = (filterKey, value) => {
        setSelectedFilters((prev) => {
            const current = prev[filterKey] || [];
            const updated = current.includes(value)
                ? current.filter((v) => v !== value)
                : [...current, value];
            // return { ...prev, [filterKey]: updated };
            const newFilters = { ...prev, [filterKey]: updated };
            return newFilters;
        });
    };

   
useEffect(() => {
    const params = new URLSearchParams(location.search);

    //  KEEP SEARCH QUERY
    if (queryText) {
        params.set("q", queryText);
    }

    // remove old filters
    Object.keys(selectedFilters).forEach((key) => {
        params.delete(key);
    });

    // add new filters
    Object.entries(selectedFilters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
            params.set(key, values.join(","));
        }
    });

    const nextSearch = params.toString();

    if (nextSearch !== location.search.replace("?", "")) {
        navigate(
            {
                pathname: location.pathname,
                search: nextSearch,
            },
            { replace: true }
        );
    }

}, [selectedFilters, queryText, location.pathname]);


    const toggleWishlist = async (e, product) => {
        toast.dismiss();
        e.stopPropagation();
        // if (!isLoggedIn) {
        //     setShowLoginPrompt(true);
        //     return;
        // }
        if (!isLoggedIn) {
            navigate("/login");
            return;
        }
        const isInWishlist = product.is_wishlisted;
        try {
            if (isInWishlist) {
                const wishlistItem = product.wishlist[0].wishlist_id;
                if (wishlistItem) {
                    await dispatch(removeFromWishlist(wishlistItem)).unwrap();
                    getCartWishlistCount(); // refresh count after add
                    setProducts((prev) =>
                        prev.map((p) =>
                            p.id === product.id
                                ? { ...p, is_wishlisted: false, wishlist: [] }
                                : p
                        )
                    );
                }
            } else {
                // addToWishlist thunk should return the new wishlist item(s)
                const addedWishlistItem = await dispatch(
                    addToWishlist({ product_id: product.id })
                ).unwrap();

                getCartWishlistCount(); // refresh count after add
                setAnimatedWish(product.id);

                // If your API returns the whole wishlist array:
                const wishlist = Array.isArray(addedWishlistItem)
                    ? addedWishlistItem.find((w) => w.product_id === product.id)
                    : addedWishlistItem;

                // Update local state immutably
                setProducts((prev) =>
                    prev.map((p) =>
                        p.id === product.id
                            ? {
                                ...p,
                                is_wishlisted: true,
                                wishlist: wishlist ? [{ wishlist_id: wishlist.id }] : [],
                            }
                            : p
                    )
                );
                setTimeout(() => setAnimatedWish(null), 1500);
            }
        } catch (err) {
            toast.error("Something went wrong. please try after sometime");
        }
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"   // remove "smooth" if you want instant scroll
        });
    };

    const renderFilterGroup = (title, masterOptions, key, isMobile = false) => {
        
        const isExpanded = expandedGroups[key] || false;
        const options = orderByString(masterOptions);
        const visibleOptions = isExpanded ? options : options?.slice(0, 5);
        const hiddenCount = options.length - visibleOptions.length;

        const currentFilters = isMobile ? tempMobileFilters : selectedFilters;
        const onChangeHandler = isMobile
            ? handleMobileFilterChange
            : handleFilterChange;

        return (
            <div className="custom-filter-group" key={key}>
                <h4>{title}</h4>

                {visibleOptions.map((opt, i) => (
                    <label key={i}>
                        <input
                            type="checkbox"
                            checked={currentFilters[key]?.includes(opt) || false}
                            onChange={() => { scrollToTop(); onChangeHandler(key, opt) }}
                        />
                        <span className="txt_checkbox">{capitalizeWords(opt.trim())}</span>
                    </label>
                ))}

                {/* Show More / Show Less row */}
                {options.length > 5 && (
                    <div
                        className="show-more-row"
                        onClick={() =>
                            setExpandedGroups((prev) => ({
                                ...prev,
                                [key]: !isExpanded,
                            }))
                        }
                    >
                        {isExpanded ? "− Show Less" : `+ Show More (${hiddenCount})`}
                    </div>
                )}
            </div>
        );
    };

    // Render category filters (with subcategories)
       const renderCategoryFilter = (categories, isMobile = false) => {
        const currentFilters = isMobile ? tempMobileFilters : selectedFilters;
        const onChangeHandler = isMobile
            ? handleMobileFilterChange
            : handleFilterChange;

        const groupKey = "categories"; 
       const isExpanded = expandedGroups[groupKey]; 
       const visibleCategories = isExpanded ? categories : categories.slice(0, 5);
       const hiddenCount = categories.length - 5;     

        return (
            categories?.length > 0 && (
                <div className="custom-filter-group" key="categories">
                    <h4>Categories</h4>

                    <div className="subcategory-list">
                        {visibleCategories.map((sub) => (
                        <label key={sub.subcategory_id}>
                            <input
                                type="checkbox"
                                value={sub.subcategories}
                                checked={
                                    currentFilters.categories?.includes(sub.subcategories) || false
                                }
                                onChange={() => {
                                    scrollToTop();
                                    onChangeHandler("categories", sub.subcategories);
                                }}
                            />

                            <span className="txt_checkbox">
                                {capitalizeWords(sub.subcategories)}
                            </span>
                        </label>
                    ))}

                       {/* Show More / Show Less row */}
                   {categories.length > 5 && (
                        <div
                            className="show-more-row"
                            onClick={() =>
                                setExpandedGroups((prev) => ({
                                    ...prev,
                                    [groupKey]: !prev[groupKey],
                                }))
                            }
                        >
                            {isExpanded
                                ? "− Show Less"
                                : `+ Show More (${hiddenCount})`}
                        </div>
                    )}


                    </div>




                </div>
            )
        );
    };
    
    // Render price range filter
    const renderPriceFilter = (priceFilter, isMobile = false) => {
        const currentFilters = isMobile ? tempMobileFilters : selectedFilters;
        const onChangeHandler = isMobile ? handleMobileFilterChange : handleFilterChange;

        const handlePriceChange = (filterValue) => {
            onChangeHandler("price_filter", filterValue);
        };

        return (
            priceFilter?.length > 0 && (
                <div className="custom-filter-group" key="price_filter">
                    <h4>Price Range</h4>

                    {priceFilter.map((price, i) => (
                        <label key={i}>
                            <input
                                type="checkbox"
                                value={price.range_label}
                                name="price_filter"
                                checked={
                                    currentFilters.price_filter?.includes(price.filter_value) || false
                                }
                                onChange={() => {
                                    scrollToTop();
                                    handlePriceChange(price.filter_value);
                                }}
                            />

                            <span className="txt_checkbox">{price.range_label}</span>
                        </label>
                    ))}
                </div>
            )
        );
    };

    // Render discount filters
    const renderDiscountFilter = (discounts, isMobile = false) => {
        if (!discounts || discounts.length === 0) return null; // ⬅ prevents empty UI
        const currentFilters = isMobile ? tempMobileFilters : selectedFilters;
        const onChangeHandler = isMobile
            ? handleMobileFilterChange
            : handleFilterChange;

        return (
            <div className="custom-filter-group" key="discount_filter">
                <h4>Discount</h4>
                {discounts.map((disc, i) => (
                    <label key={i}>
                        <input
                            type="checkbox" value={disc.total_items} name="discount_filter"
                            checked={
                                currentFilters.discount_filter?.includes(disc.discount_range) ||
                                false
                            }
                            onChange={() => { scrollToTop(); onChangeHandler("discount_filter", disc.discount_range) }}
                        />
                        <span className="txt_checkbox">
                            {disc.discount_range}
                        </span>
                    </label>
                ))}
            </div>
        );
    };

    const handleMobileFilterChange = (filterKey, value) => {
        setTempMobileFilters((prev) => {
            const current = prev[filterKey] || [];
            const updated = current.includes(value)
                ? current.filter((v) => v !== value)
                : [...current, value];
            // return { ...prev, [filterKey]: updated };
            const newFilters = { ...prev, [filterKey]: updated };
            // Save to localStorage
            return newFilters;
        });
    };
    useEffect(() => {
        if (isFilterOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
    }, [isFilterOpen]);

    const handleSelect = (option, key) => {
        setSelected(option);
        setShowShort(key);
        setSelectedFilters((prev) => {
            const newFilters = {
                ...prev,
                sort_by: [option],
            };
            return newFilters;
        });
    };

    const hasActiveFilters = Object.values(selectedFilters).some(
        (value) => Array.isArray(value) && value.length > 0
    );

    // ===== MOBILE FILTER HANDLERS ==========

    const applyMobileFilters = () => {
        setSelectedFilters(tempMobileFilters);
        setIsFilterOpen(false);
        scrollToTop();
    };
    // Clear mobile filters: reset temp filters
    const clearMobileFilters = () => {
        setTempMobileFilters({});
    };

    return (
        <>
            <ToastContainer position="top-right" style={{ zIndex: 9999999999999 }} autoClose={3000} limit={1} hideProgressBar={true} transition={Slide} newestOnTop={true} />
            <div style={{ position: "relative", right: "6px" }}>

                  <PromotionBreadcrumbs promotionType={promotionType} query={queryText} />
                  
            </div>
            {/* MOBILE FILTER BUTTON */}
            {products.length > 0 &&
                <div className="track_filter">
                    <div className="root_btn_filter_hader">
                        <div className="mobile-filter-btn" onClick={() => setIsFilterOpen(true)}>
                            <i className="bi bi-sliders"></i> FILTERS
                        </div>
                        <MobileFilter
                            isOpen={isFilterOpen}
                            onClose={() => setIsFilterOpen(false)}
                            filters={filters}
                            applyFilters={applyMobileFilters}
                            clearFilters={clearMobileFilters}
                            renderCategoryFilter={renderCategoryFilter}
                            renderPriceFilter={renderPriceFilter}
                            renderDiscountFilter={renderDiscountFilter}
                            renderFilterGroup={renderFilterGroup}
                            tempFilters={tempMobileFilters}
                            setTempFilters={setTempMobileFilters}
                        />
                        {/* shop by */}
                        <div className="sortby-container">
                            <div className="dropdown">
                                <div
                                    className="dropdown-toggle sortby-btn"
                                    id="dropdownMenuButton"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    <i className="bi bi-sort-down"></i> Sort By
                                </div>
                                <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                    {Object.entries(sorting).map(([key, label]) => (
                                        <li key={key}>
                                            <button
                                                className={`dropdown-item ${selected === key ? "active-option" : ""}`}
                                                onClick={() => handleSelect(key, label)}
                                            >
                                                {label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            }
            <div className="custom-products-page">
                <aside className="custom-filters filter-section-fixed">
                    {products.length > 0 &&
                        <>
                            <div className="root_devider_flt">
                                {dataReady ? (<Skeleton height={22} width={80} style={{ marginBottom: 5 }} />) : (
                                    <>
                                        <h2>Filters</h2>
                                        {hasActiveFilters && (
                                            <p
                                                className="clr-all"
                                                onClick={() => {
                                                    setSelectedFilters({});
                                                    setTempMobileFilters({});
                                                }}
                                            >
                                                Clear all
                                            </p>
                                        )}

                                    </>
                                )}
                            </div>
                        </>
                    }
                    {dataReady ? (<SkeletonFilter />) : filters && Object.keys(filters).length > 0 ? (
                        //  Render filters when available
                        <>
                            {/* 1️ Category Filter */}
                            {filters.categories && renderCategoryFilter(filters.categories)}
                            {/* 2️ Price Filter */}
                            {filters.price_filter && renderPriceFilter(filters.price_filter)}
                            {/* 3️ Discount Filter */}
                            {filters.discount_filter && renderDiscountFilter(filters.discount_filter)}
                            {/* 4️ Product Filters */}
                            {filters.product_filter &&
                                Object.entries(filters.product_filter)
                                    .filter(([key, values]) => Array.isArray(values) && values.length > 0)
                                    .map(([key, values]) =>
                                        renderFilterGroup(key.replace(/_/g, " "), values, key)
                                    )}
                        </>
                    ) : (  //  No filters found            
                        <p className="no-filters">No filters.</p>
                    )}
                </aside>
                <main className="custom-product-list">
                    {/* SORT BAR */}
                    {products.length > 0 &&
                        <>
                            <div className="sortby-container-mlb">
                                <div>
                                    {dataReady ? (
                                        <Skeleton height={28} width={180} style={{ marginBottom: 10 }} />
                                    ) : (
                                        <span className="track_contuing product-heading">
                                            <span className="product-title" style={{ fontSize: "20px" }}>
                                                {filterTitle}
                                            </span>
                                            {/* &nbsp;&nbsp;
                                            <span className="text-muted product-range" style={{ fontSize: "12px" }}>
                                                {`(Showing ${rangeStart}-${rangeEnd} products of ${total} products)`}
                                            </span> */}
                                        </span>
                                    )}
                                </div>

                                <div className="dropdown" style={{ display: "flex", gap: "10px" }}>
                                    <div
                                        className="dropdown-toggle sortby-btn"
                                        id="dropdownMenuButton"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Sort By &nbsp;
                                        <div className="sort-name">
                                            {showShort}
                                        </div>
                                    </div>
                                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                        {Object.entries(sorting).map(([key, label]) => (
                                            <li key={key}>
                                                <button
                                                    className={`dropdown-item ${selected === key ? "active-option" : ""}`}
                                                    onClick={() => handleSelect(key, label)}
                                                >
                                                    {label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </>
                    }
                    <div className="mb-6 mt-1">
                        {dataReady ? (
                            /* LOADING */
                            <SkeletonProduct />
                        ) : products?.length > 0 ? (
                            /* PRODUCTS */
                            <div className="product-grid">
                                {products.map((item, index) => {
                                    const isWishlisted = item.is_wishlisted;
                                    return (
                                        <Fragment key={item.id || index}>
                                            {/* PRODUCT CARD */}
                                            <div
                                                className="product-card-dtl pointer-crusser"
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div
                                                    className="product-img-box product-img-box001"
                                                    onClick={() =>
                                                        window.open(
                                                            getProductPdpUrl(item.action_url, item.sku),
                                                            "_blank",
                                                            "noopener,noreferrer"
                                                        )
                                                    }
                                                >
                                                    <img
                                                        src={item.media_list?.main?.file}
                                                        alt={item.name}
                                                        title={item.name}
                                                        className="main_image"
                                                        effect="blur"
                                                    />
                                                    <img
                                                        src={item.media_list?.hover?.file}
                                                        alt={item.name}
                                                        title={item.name}
                                                        className="hover_image"
                                                    />

                                                    {/* Wishlist Button */}
                                                    <button
                                                        className="wishlist-btn_products whislist-box001 pointer-crusser"
                                                        onClick={(e) => toggleWishlist(e, item)}
                                                    >
                                                        {animatedWish === item.id ? (
                                                            <div
                                                                style={{
                                                                    width: 20,
                                                                    height: 24,
                                                                    overflow: "hidden",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                }}
                                                            >
                                                                <Player
                                                                    autoplay
                                                                    keepLastFrame
                                                                    src={heartAnimation}
                                                                    style={{
                                                                        width: 139,
                                                                        height: 139,
                                                                        transform: "scale(0.5)",
                                                                        transformOrigin: "center",
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <Heart
                                                                color={isWishlisted ? "#FF0000" : "#000"}
                                                                fill={isWishlisted ? "#FF0000" : "none"}
                                                                size={20}
                                                                strokeWidth={2}
                                                            />
                                                        )}
                                                    </button>

                                                    {/* Quick View */}
                                                    <div className="qucick_dv">
                                                        <span
                                                            className="quick-view_pd"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setQuickViewProduct(item);
                                                                setShowModal(true);
                                                            }}
                                                        >
                                                            Quick View &nbsp;
                                                            <Expand color="#000000" size={15} strokeWidth={1.25} />
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Product Title */}
                                                <p className="product-title pointer-crusser">
                                                    <Link
                                                        to={getProductPdpUrl(item.action_url, item.sku)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {limitChars(item.name, 24)}
                                                    </Link>
                                                </p>

                                                {/* Product Price */}
                                                <Link
                                                    to={getProductPdpUrl(item.action_url, item.sku)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <div className="product-price">
                                                        <span>{formatPrice(item.selling_price)}</span>
                                                        {item.discount_percent > 0 && (
                                                            <>
                                                                <span className="original">
                                                                    {formatPrice(item.mrp)}
                                                                </span>
                                                                <span
                                                                    className="discount"
                                                                    style={{ fontSize: "14px", fontWeight: "500" }}
                                                                >
                                                                    {formatDiscount(item.discount_percent)}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </Link>
                                            </div>

                                            {/*---- BESTSELLER BANNER AFTER TOP 10 PRODUCTS--- */}

                                            {isBestsellerPage &&
                                                index === 9 &&
                                                bannerStatus === "succeeded" &&
                                                banner && (
                                                    <div className="product-poster-card">
                                                        <Link to={banner.action_url || "#"}>
                                                            <img
                                                                src={banner.media || emptyproduct}
                                                                alt={banner.title || "Bestsellers Banner"}
                                                                className="banner-image"
                                                            />
                                                        </Link>
                                                    </div>
                                                )}
                                        </Fragment>
                                    );
                                })}
                            </div>
                        ) : (<EmptyProducts emptyproduct={emptyproduct} />)}
                    </div>
                    {/* PAGINATION */}
                    <div className="pagination_track">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            totalitems={products.length}
                        />
                    </div>
                </main>

                <ProductQuickViewModal show={showModal} onHide={() => setShowModal(false)} product={quickViewProduct} />
            </div>

            {/* login modal */}
            {showLoginPrompt && (
                <LoginPromptModal onClose={() => setShowLoginPrompt(false)} />
            )}
            {!dataReady && (
                <>
                    <TopPickSection />
                    <CustomerFavourite />
                    <Footer />
                </>
            )}
        </>
    );
};
export default PromotionsPage;

